import { useMemo, useReducer, useRef } from 'react';
import { serializeActor } from 'ui-product-approvals-common/utils/actorKey';
import { ApprovalRules } from 'ui-product-approvals-common/types/ApprovalRules';
import { addingStepChangesLockedOrder, hasValidStepReorderIndexes, haveLockedSerializedOrdersChanged, isStepGroupComplete, removingStepChangesLockedOrder, reorderChangesLockedOrder, stepHasLockedApprover } from '../utils/approvalStepsUtils';
import { MAX_STAGES } from '../utils/approvalProcessValidation';
const EMPTY_LOCKED_ACTOR_KEYS = new Set();
const EMPTY_LOCKED_STAGE_INDEXES = new Set();
const makeStep = (seq, approvers = [], templateStageLocked = false) => ({
  id: `step-${seq}`,
  approvers,
  templateStageLocked
});
export const initApprovalStepsState = (groups, startSeq = 0, lockedStageIndexes = EMPTY_LOCKED_STAGE_INDEXES) => {
  if (groups.length === 0) {
    return {
      steps: [makeStep(startSeq)],
      seq: startSeq + 1
    };
  }
  return {
    steps: groups.map((approvers, index) => makeStep(startSeq + index, approvers, lockedStageIndexes.has(index))),
    seq: startSeq + groups.length
  };
};
const moveInArray = (arr, fromIndex, toIndex) => {
  if (!hasValidStepReorderIndexes(arr.length, fromIndex, toIndex)) {
    return arr;
  }
  const next = [...arr];
  const [item] = next.splice(fromIndex, 1);
  next.splice(toIndex, 0, item);
  return next;
};
export const approvalStepsReducer = (state, action) => {
  switch (action.type) {
    case 'ADD_STEP':
      {
        const steps = [...state.steps];
        const atIndex = action.atIndex === undefined ? steps.length : Math.min(Math.max(action.atIndex, 0), steps.length);
        steps.splice(atIndex, 0, makeStep(state.seq));
        return {
          steps,
          seq: state.seq + 1
        };
      }
    case 'REMOVE_STEP':
      return Object.assign({}, state, {
        steps: state.steps.filter(step => step.id !== action.stepId)
      });
    case 'REORDER_STEPS':
      {
        const steps = moveInArray(state.steps, action.fromIndex, action.toIndex);
        return steps === state.steps ? state : Object.assign({}, state, {
          steps
        });
      }
    case 'SET_STAGE_ORDER':
      return Object.assign({}, state, {
        steps: action.steps
      });
    case 'ADD_APPROVER':
      return Object.assign({}, state, {
        steps: state.steps.map(step => step.id === action.stepId && !step.approvers.some(approver => serializeActor(approver.actor) === serializeActor(action.approver.actor)) ? Object.assign({}, step, {
          approvers: [...step.approvers, action.approver]
        }) : step)
      });
    case 'REMOVE_APPROVER':
      return Object.assign({}, state, {
        steps: state.steps.map(step => step.id === action.stepId ? Object.assign({}, step, {
          approvers: step.approvers.filter(approver => serializeActor(approver.actor) !== action.actorKey)
        }) : step)
      });
    case 'MOVE_APPROVER':
      {
        const destinationStep = state.steps.find(step => step.id === action.toStepId);
        if (!destinationStep) {
          return state;
        }
        let moved;
        const withoutApprover = state.steps.map(step => {
          const found = step.approvers.find(approver => serializeActor(approver.actor) === action.actorKey);
          if (!found) {
            return step;
          }
          moved = found;
          return Object.assign({}, step, {
            approvers: step.approvers.filter(approver => serializeActor(approver.actor) !== action.actorKey)
          });
        });
        if (!moved) {
          return state;
        }
        const movedApprover = moved;
        return Object.assign({}, state, {
          steps: withoutApprover.map(step => {
            if (step.id !== action.toStepId) {
              return step;
            }
            const approvers = [...step.approvers];
            const insertIndex = action.toIndex === undefined ? approvers.length : Math.min(Math.max(action.toIndex, 0), approvers.length);
            approvers.splice(insertIndex, 0, movedApprover);
            return Object.assign({}, step, {
              approvers
            });
          })
        });
      }
    case 'INIT':
      return initApprovalStepsState(action.groups, state.seq);
    default:
      return state;
  }
};
export const useApprovalSteps = (initialGroups = [], lockedActorKeys = EMPTY_LOCKED_ACTOR_KEYS, lockedStageIndexes = EMPTY_LOCKED_STAGE_INDEXES, stepListLocked = false, approvalRules = ApprovalRules.NON_SEQUENTIAL) => {
  const [state, dispatch] = useReducer(approvalStepsReducer, initialGroups, groups => initApprovalStepsState(groups, 0, lockedStageIndexes));

  // Already-approved approvers (and the steps containing them) are locked: they
  // can't be removed, moved, or have their step order changed. The latest set
  // and state are read through refs so the guards stay correct as
  // approvedApproverActorKeys loads, while keeping the action creators stable.
  const lockedActorKeysRef = useRef(lockedActorKeys);
  // eslint-disable-next-line react-hooks/refs
  lockedActorKeysRef.current = lockedActorKeys;
  const stepListLockedRef = useRef(stepListLocked);
  // eslint-disable-next-line react-hooks/refs
  stepListLockedRef.current = stepListLocked;
  const approvalRulesRef = useRef(approvalRules);
  // eslint-disable-next-line react-hooks/refs
  approvalRulesRef.current = approvalRules;
  const stateRef = useRef(state);
  // eslint-disable-next-line react-hooks/refs
  stateRef.current = state;
  const actions = useMemo(() => {
    const canRemoveApprover = (stepId, actorKey) => {
      const step = stateRef.current.steps.find(currentStep => currentStep.id === stepId);
      if (!step || stepListLockedRef.current || lockedActorKeysRef.current.has(actorKey) || step.templateStageLocked || !step.approvers.some(approver => serializeActor(approver.actor) === actorKey)) {
        return false;
      }
      return !haveLockedSerializedOrdersChanged(stateRef.current.steps, approvalStepsReducer(stateRef.current, {
        type: 'REMOVE_APPROVER',
        stepId,
        actorKey
      }).steps, lockedActorKeysRef.current);
    };
    const canMoveApprover = args => {
      const sourceStep = stateRef.current.steps.find(step => step.approvers.some(approver => serializeActor(approver.actor) === args.actorKey));
      const destinationStep = stateRef.current.steps.find(step => step.id === args.toStepId);
      if (stepListLockedRef.current || !sourceStep || !destinationStep || lockedActorKeysRef.current.has(args.actorKey) || sourceStep.templateStageLocked || destinationStep.templateStageLocked ||
      // A stage with a locked approver is complete and cannot receive moves.
      isStepGroupComplete(destinationStep, lockedActorKeysRef.current, approvalRulesRef.current)) {
        return false;
      }
      return !haveLockedSerializedOrdersChanged(stateRef.current.steps, approvalStepsReducer(stateRef.current, Object.assign({
        type: 'MOVE_APPROVER'
      }, args)).steps, lockedActorKeysRef.current);
    };
    const canRemoveStep = stepId => {
      if (stepListLockedRef.current) {
        return false;
      }
      const stepIndex = stateRef.current.steps.findIndex(step => step.id === stepId);
      const step = stateRef.current.steps[stepIndex];
      if (!step) {
        return false;
      }
      return !(step.templateStageLocked || stepHasLockedApprover(step, lockedActorKeysRef.current) || removingStepChangesLockedOrder(stateRef.current.steps, stepIndex, lockedActorKeysRef.current));
    };
    return {
      addStep: atIndex => {
        if (stepListLockedRef.current) {
          return;
        }
        if (stateRef.current.steps.length >= MAX_STAGES) {
          return;
        }
        const insertionIndex = Math.min(Math.max(atIndex !== null && atIndex !== void 0 ? atIndex : stateRef.current.steps.length, 0), stateRef.current.steps.length);
        if (addingStepChangesLockedOrder(stateRef.current.steps, insertionIndex, lockedActorKeysRef.current)) {
          return;
        }
        dispatch({
          type: 'ADD_STEP',
          atIndex: insertionIndex
        });
      },
      canRemoveStep,
      removeStep: stepId => {
        if (!canRemoveStep(stepId)) {
          return;
        }
        dispatch({
          type: 'REMOVE_STEP',
          stepId
        });
      },
      reorderSteps: (fromIndex, toIndex) => {
        if (stepListLockedRef.current || reorderChangesLockedOrder(stateRef.current.steps, fromIndex, toIndex, lockedActorKeysRef.current)) {
          return;
        }
        dispatch({
          type: 'REORDER_STEPS',
          fromIndex,
          toIndex
        });
      },
      // Array-based reorder for the redesigned builder, which reports a full
      // new order rather than indices. Its reorderable runs are confined by
      // locked stages, so the order can never move a locked stage — no
      // per-move veto is needed (the legacy `reorderSteps` still guards).
      setStageOrder: steps => {
        if (stepListLockedRef.current) {
          return;
        }
        dispatch({
          type: 'SET_STAGE_ORDER',
          steps
        });
      },
      addApprover: (stepId, approver) => {
        const step = stateRef.current.steps.find(currentStep => currentStep.id === stepId);
        const action = {
          type: 'ADD_APPROVER',
          stepId,
          approver
        };
        if (!step || stepListLockedRef.current || step.templateStageLocked || haveLockedSerializedOrdersChanged(stateRef.current.steps, approvalStepsReducer(stateRef.current, action).steps, lockedActorKeysRef.current)) {
          return;
        }
        dispatch(action);
      },
      canRemoveApprover,
      removeApprover: (stepId, actorKey) => {
        if (!canRemoveApprover(stepId, actorKey)) {
          return;
        }
        dispatch({
          type: 'REMOVE_APPROVER',
          stepId,
          actorKey
        });
      },
      canMoveApprover,
      moveApprover: args => {
        if (!canMoveApprover(args)) {
          return;
        }
        dispatch(Object.assign({
          type: 'MOVE_APPROVER'
        }, args));
      },
      init: groups => dispatch({
        type: 'INIT',
        groups
      })
    };
  }, []);
  return Object.assign({
    steps: state.steps
  }, actions);
};